import type { KyInstance } from 'ky';

import { convertToQueryString, ensureError, flattenFilterConditions, isDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { MissingListQuery } from './types/missing-list-query.type';
import type { MissingMailingsPagedData } from './types/missing-mailings-paged-data.type';

/**
 * Fetches a paged set of missing mailings (package-grain) from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param searchParams - The query parameters for paging, filtering, and the
 *   Missing-Lists toolbar (search / status). Reuses {@link MissingListQuery} —
 *   the package endpoint takes the same inputs as the list endpoint.
 * @returns A promise that resolves to an operation result containing the paged
 *   missing-mailings envelope, including the sibling status-count fields.
 * @throws Will throw an error if the request fails.
 */
export const getMissingMailings = async (instance: KyInstance, searchParams: MissingListQuery) => {
  try {
    const { filter, sort, ...restParams } = searchParams;
    const flattenedFilters = isDefined(filter) ? flattenFilterConditions(filter) : [];

    return await instance
      .get<OperationResultWithData<MissingMailingsPagedData>>(Uri.mailing.getMissingMailings, {
        searchParams: {
          ...restParams,
          ...convertToQueryString(flattenedFilters, 'filter'),
          ...convertToQueryString(sort, 'sort'),
        },
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
